import { Controller } from "stimulus";
import sanitizeHtml from "sanitize-html";

export default class extends Controller {

  /**
   * Wires up the Continue/Submit buttons and a document-level click listener that resets the
   * reason-question state whenever the consumer navigates away via the QLE menu.
   * Runs automatically once, when Stimulus attaches to the element carrying
   * `data-controller="sep-application"`.
   *
   * @returns {void}
   */
  connect() {
      this.submitButton = this.element.querySelector('#qle_submit');
      this.submitButton.addEventListener('click', () => this.evaluateQleEligibility());
      this.qleSuccessSubmit = this.element.querySelector('#qle_success_submit');
      this.qleSuccessSubmit.addEventListener('click', (event) => this.createApplication(event));

      this.handleQleMenuClick = (event) => {
        if (event.target.closest('a.qle-menu-item, a.existing-sep-item')) {
          this.resetReasonState();
        }
      };
      document.addEventListener('click', this.handleQleMenuClick);
  }

  /**
   * Removes the document-level click listener registered in {@link connect}, so it doesn't
   * leak once Stimulus detaches this controller from its element.
   *
   * @returns {void}
   */
  disconnect() {
    document.removeEventListener('click', this.handleQleMenuClick);
  }

  /**
   * Clears the reason-specific follow-up question UI (question markup, effective-on-kinds
   * field, reason-choice field) and re-enables the success-submit button.
   *
   * @returns {void}
   */
  resetReasonState() {
    const qleReason = document.getElementById('qle_reason');
    qleReason.style.display = 'none';
    qleReason.innerHTML = '';

    const effectiveOnKindsContainer = document.querySelector('#qle-details .effective_on_kinds');
    const specReasonsContainer = document.querySelector('#qle-details .spec_reasons');
    if (effectiveOnKindsContainer) effectiveOnKindsContainer.innerHTML = '';
    if (specReasonsContainer) specReasonsContainer.innerHTML = '';

    this.qleSuccessSubmit.removeAttribute('disabled');
  }

  /**
   * Resets the entire QLE-detail form back to its initial state: clears the reason-question
   * state (via {@link resetReasonState}), hides the success/error info panels, shows the
   * date-entry step again, and clears the QLE date field.
   *
   * @returns {void}
   */
  resetAll() {
    this.resetReasonState();

    const successInfo = document.querySelector('#qle-details .success-info');
    const errorInfo = document.querySelector('#qle-details .error-info');
    const qleDateChose = document.getElementById('qle-date-chose');

    if (successInfo) successInfo.classList.add('hidden');
    if (errorInfo) errorInfo.classList.add('hidden');
    if (qleDateChose) qleDateChose.classList.remove('hidden');

    const qleDate = document.getElementById('qle_date');
    if (qleDate) qleDate.value = '';
  }

  /**
   * Determines whether a click event originated from the shared "Previous" navigation button.
   *
   * @param {MouseEvent} event The click event to inspect.
   * @returns {boolean} `true` if the click was on (or inside) `#previous_button`.
   */
  isPreviousStepClick(event) {
    return !!event.target.closest('#previous_button');
  }

  /**
   * Submits the SEP application to `POST /sep/applications` using the current form field
   * values (or the given overrides), and redirects to the returned `redirect_url` on success.
   * Called both directly (Submit button click) and automatically by
   * {@link handleEligibilityResponse} when the eligibility check comes back not-qualified (a
   * denied application is still recorded server-side).
   *
   * @param {Event|null} event The triggering click event, if any; `preventDefault()`-ed when present.
   * @param {string} [reasonOverride] Overrides the checked `input[name="reason"]` value, when provided.
   * @param {string} [oldZipOverride] Overrides the `#old_zip` field value, when provided.
   * @param {string} [newZipOverride] Overrides the `#new_zip` field value, when provided.
   * @returns {void}
   */
  createApplication(event, reasonOverride, oldZipOverride, newZipOverride) {
    if (event) event.preventDefault();

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const qleDate = document.getElementById('qle_date').value;
    const qleId = document.getElementById('qle_id').value;
    const reason = reasonOverride !== undefined ? reasonOverride : document.querySelector('input[name="reason"]:checked')?.value;
    const oldZip = oldZipOverride !== undefined ? oldZipOverride : document.getElementById('old_zip')?.value;
    const newZip = newZipOverride !== undefined ? newZipOverride : document.getElementById('new_zip')?.value;
    const effectiveOnKind = document.getElementById('effective_on_kind')?.value;
    const changePlan = document.getElementById('change_plan')?.value;
    const marketKind = document.getElementById('market_kind')?.value;
    const qleReasonChoice = document.getElementById('qle_reason_choice')?.value;

    fetch('/sep_applications', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        effective_on_kind: effectiveOnKind,
        qle_date: qleDate,
        qle_id: qleId,
        change_plan: changePlan,
        market_kind: marketKind,
        qle_reason_choice: qleReasonChoice,
        qle_reason_val: reason,
        old_zip: oldZip,
        new_zip: newZip })
    }).then(response => response.json()).then(data => {
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        console.error('SEP application create failed');
      }
    });
  }

  /**
   * Handles submission of a QLE reason-specific follow-up question. If the checked reason
   * requires a zip comparison (`.zip-check`), reveals the zip-compare fields instead of
   * proceeding; otherwise re-runs {@link checkDate} with the answered reason included.
   *
   * @param {Event} event The form/button submit event; `preventDefault()`-ed.
   * @returns {void}
   */
  submitReason(event) {
    event.preventDefault();

    if (this.isPreviousStepClick(event)) {
      this.resetAll();
      return;
    }

    const checkedReason = document.querySelector('input[name="reason"]:checked');
    if (checkedReason && checkedReason.classList.contains('zip-check')) {
      const specialReasons = document.querySelector('#qle_reason .special_qle_reasons');
      if (specialReasons) specialReasons.style.display = 'none';

      const zipCompare = document.getElementById('moving_zip_compare');
      if (zipCompare) zipCompare.classList.remove('d-none');
      return;
    }

    this.evaluateQleEligibility();
  }

  /**
   * Handles submission of the old-zip/new-zip comparison fields (shown for the "moved" QLE
   * reason), re-running {@link checkDate} with the entered zips included.
   *
   * @param {Event} event The form/button submit event; `preventDefault()`-ed.
   * @returns {void}
   */
  submitZipCompare(event) {
    event.preventDefault();

    if (this.isPreviousStepClick(event)) {
      this.resetAll();
      return;
    }

    this.evaluateQleEligibility();
  }

  /**
   * Checks whether the current QLE date/id/reason/zips currently qualify for a SEP, via
   * `POST /sep/applications/evaluate_qle_eligibility`, and hands the response to
   * {@link handleEligibilityResponse}. Does not create a {@link SepApplication}.
   *
   * @returns {void}
   */
  evaluateQleEligibility() {
    const qleDate = document.getElementById('qle_date').value;
    const qleId = document.getElementById('qle_id').value;
    const reason = document.querySelector('input[name="reason"]:checked')?.value;
    const oldZip = document.getElementById('old_zip')?.value;
    const newZip = document.getElementById('new_zip')?.value;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    fetch('/sep_applications/evaluate_qle_eligibility', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ date_val: qleDate, qle_id: qleId, qle_reason_val: reason, old_zip: oldZip, new_zip: newZip })
    }).then(response => response.json())
        .then(data => this.handleEligibilityResponse(data))
        .catch(() => console.error('Error checking QLE eligibility'));
  }

  /**
   * Renders the effective-on-kind field for the matched QLE: a `<select>` when the QLE offers
   * more than one effective-on-kind option (disabling `successSubmit` until one is chosen), or
   * a single hidden input pre-filled with the only option otherwise.
   *
   * @param {string[]} effectiveOnKinds The QLE's available `effective_on_kinds` values.
   * @param {Element} container The `.effective_on_kinds` element to render into.
   * @param {Element} successSubmit The Submit button whose disabled state is managed here.
   * @returns {void}
   */
  renderEffectiveOnKindsField(effectiveOnKinds, container, successSubmit) {
    if (effectiveOnKinds.length > 1) {
      successSubmit.setAttribute('disabled', 'disabled');

      const select = document.createElement('select');
      select.id = 'effective_on_kind';

      const blankOption = document.createElement('option');
      blankOption.value = '';
      blankOption.textContent = 'Select';
      select.appendChild(blankOption);

      effectiveOnKinds.forEach(kind => {
        const option = document.createElement('option');
        option.value = kind;
        option.textContent = kind;
        select.appendChild(option);
      });

      container.replaceChildren();
      container.appendChild(select);

      select.addEventListener('change', () => {
        if (select.value) {
          successSubmit.removeAttribute('disabled');
        } else {
          successSubmit.setAttribute('disabled', 'disabled');
        }
      });
    } else {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.id = 'effective_on_kind';
      hidden.value = effectiveOnKinds[0];
      container.replaceChildren();
      container.appendChild(hidden);
      successSubmit.removeAttribute('disabled');
    }
  }

  /**
   * Renders a hidden `#qle_reason_choice` input carrying the consumer's answered reason value,
   * so it's submitted along with {@link createApplication}'s request.
   *
   * @param {string|undefined} qleReasonVal The answered reason value, if any.
   * @param {Element} container The `.spec_reasons` element to render into.
   * @returns {void}
   */
  renderQleReasonChoiceField(qleReasonVal, container) {
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.id = 'qle_reason_choice';
    hidden.value = qleReasonVal || '';
    container.replaceChildren();
    container.appendChild(hidden);
  }

  /**
   * Updates the QLE-detail UI based on the `evaluate_qle_eligibility` response: shows the
   * reason-specific follow-up question when one is required and unanswered, shows the
   * success panel (with effective-on-kind/reason-choice fields) once qualified and answered,
   * or shows the not-eligible error panel and submits a (denied) application anyway via
   * {@link createApplication} when the date doesn't qualify.
   *
   * @param {Object} data The parsed JSON response from `evaluate_qle_eligibility`.
   * @param {boolean} data.qualified_date Whether the QLE date currently qualifies for a SEP.
   * @param {string} [data.reason_question_html] Follow-up question partial HTML, if one applies.
   * @param {string} [data.qle_reason_val] The consumer's answered reason value, if answered.
   * @param {Object} [data.qle] The serialized matched QLE (id, effective_on_kinds, reason, etc.).
   * @returns {void}
   */
  handleEligibilityResponse(data) {
    const successInfo = document.querySelector('#qle-details .success-info');
    const errorInfo = document.querySelector('#qle-details .error-info');
    const errorText = document.querySelector('#qle-details .error-text');
    const qleDateChose = document.getElementById('qle-date-chose');
    const successSubmit = document.getElementById('qle_success_submit');
    const effectiveOnKindsContainer = document.querySelector('#qle-details .effective_on_kinds');
    const specReasonsContainer = document.querySelector('#qle-details .spec_reasons');

    qleDateChose.classList.add('hidden');

    if (data.qualified_date && data.reason_question_html) {
      errorInfo.classList.add('hidden');

      const qleReason = document.getElementById('qle_reason');
      qleReason.innerHTML = this.sanitize(data.reason_question_html);
      qleReason.style.display = 'block';
      qleReason.querySelector('.special_qle_reasons')?.classList.remove('hidden');
    } else if (data.qualified_date && data.qle_reason_val && data.qle_reason_val.length > 0) {
      // Reason question was answered, and the answer wasn't disqualifying.
      successInfo.classList.remove('hidden');
      errorInfo.classList.add('hidden');

      const qleReason = document.getElementById('qle_reason');
      qleReason.style.display = 'none';
      qleReason.innerHTML = '';

      if (data.qle && data.qle.effective_on_kinds && data.qle.effective_on_kinds.length > 0) {
        this.renderEffectiveOnKindsField(data.qle.effective_on_kinds, effectiveOnKindsContainer, successSubmit);
      }
      this.renderQleReasonChoiceField(data.qle_reason_val, specReasonsContainer);
    } else if (data.qualified_date) {
      // No reason question involved for this QLE at all.
      successInfo.classList.remove('hidden');
      errorInfo.classList.add('hidden');

      const qleReason = document.getElementById('qle_reason');
      qleReason.style.display = 'none';
      qleReason.innerHTML = '';

      if (data.qle && data.qle.effective_on_kinds && data.qle.effective_on_kinds.length > 0) {
        this.renderEffectiveOnKindsField(data.qle.effective_on_kinds, effectiveOnKindsContainer, successSubmit);
      }
      this.renderQleReasonChoiceField(data.qle_reason_val, specReasonsContainer);
    } else {
      const reasonVal = document.querySelector('input[name="reason"]:checked')?.value;
      const oldZipVal = document.getElementById('old_zip')?.value;
      const newZipVal = document.getElementById('new_zip')?.value;
      if (data.qle && data.qle.effective_on_kinds && data.qle.effective_on_kinds.length > 0) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.id = 'effective_on_kind';
        hidden.value = data.qle.effective_on_kinds[0];
        effectiveOnKindsContainer.replaceChildren();
        effectiveOnKindsContainer.appendChild(hidden);
      }
      if (successInfo) successInfo.classList.add('hidden');
      if (errorInfo) errorInfo.classList.remove('hidden');
      if (successSubmit) successSubmit.setAttribute('disabled', 'disabled');
      const qleReason = document.getElementById('qle_reason');
      if (qleReason) {
        qleReason.style.display = 'none';
        qleReason.innerHTML = '';
      }
      if (errorText) {
        errorText.textContent = this.data.get('notEligibleText');
      }

      this.createApplication(null, reasonVal, oldZipVal, newZipVal);
    }
  }

  /**
   * Scrubs server-rendered reason-question markup before it's inserted into the DOM.
   *
   * The allowlist is deliberately wider than sanitize-html's defaults: the reason-question
   * partials (sep/applications/_marriage_fields, _insurance_fields, _moving_fields,
   * _zip_compare and shared/_progress_navigation_buttons) are form markup, and the defaults
   * strip every attribute except a[href|name|target]. `data-*` carries the Stimulus
   * `data-action` bindings for the Continue buttons, `class` carries `d-none`/`zip-check`,
   * and `id` carries the element ids this controller queries by — dropping any of the three
   * breaks the flow silently rather than noisily.
   *
   * @param {string} dirty The server-rendered HTML fragment.
   * @returns {string} The sanitized fragment.
   */
  sanitize(dirty) {
    const allowedTags = sanitizeHtml.defaults.allowedTags.concat([
      'input', 'label', 'select', 'option', 'button', 'fieldset', 'legend'
    ]);

    return sanitizeHtml(dirty, {
      allowedTags: allowedTags,
      allowedAttributes: {
        '*': [ 'class', 'id', 'data-*', 'aria-*' ],
        'input': [ 'type', 'name', 'id', 'value', 'checked', 'class', 'data-*' ],
        'label': [ 'for', 'class' ],
        'select': [ 'name', 'id', 'class', 'data-*' ],
        'option': [ 'value', 'selected' ],
        'a': [ 'href', 'target', 'rel', 'class' ],
        'button': [ 'id', 'type', 'class', 'disabled', 'tabindex', 'data-*' ]
      }
    });
  }

}